import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const ProductCategories = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    parent_id: null,
    sort_order: '0',
    is_active: true,
    seo_title: '',
    seo_description: '',
    image_url: ''
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ecommerce/product-categories');
      setGroups(response.data.categories || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch product categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (parentId = null) => {
    setCurrentGroup(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      parent_id: parentId,
      sort_order: '0',
      is_active: true,
      seo_title: '',
      seo_description: '',
      image_url: ''
    });
    setShowDialog(true);
  };

  const handleEdit = (group) => {
    setCurrentGroup(group);
    setFormData({
      name: group.name || '',
      slug: group.slug || '',
      description: group.description || '',
      parent_id: group.parent_id,
      sort_order: group.sort_order?.toString() || '0',
      is_active: group.is_active !== undefined ? group.is_active : true,
      seo_title: group.seo_title || '',
      seo_description: group.seo_description || '',
      image_url: group.image_url || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this group? This will also delete all sub-groups.')) return;
    try {
      await api.delete(`/ecommerce/product-categories/${id}`);
      setSuccess('Product group deleted');
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        parent_id: formData.parent_id || null,
        sort_order: parseInt(formData.sort_order) || 0
      };

      if (currentGroup) {
        await api.put(`/ecommerce/product-categories/${currentGroup.id}`, submitData);
        setSuccess('Product group updated');
      } else {
        await api.post('/ecommerce/product-categories', submitData);
        setSuccess('Product group created');
      }
      setShowDialog(false);
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
  };

  const toggleExpand = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const buildHierarchy = (groups, parentId = null, level = 0) => {
    return groups
      .filter(g => g.parent_id === parentId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map(group => ({
        ...group,
        level,
        children: buildHierarchy(groups, group.id, level + 1)
      }));
  };

  const renderGroupRow = (group) => {
    const hasChildren = group.children && group.children.length > 0;
    const isExpanded = expandedGroups.has(group.id);
    const indent = group.level * 2;

    return (
      <React.Fragment key={group.id}>
        <tr style={{ borderBottom: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`, backgroundColor: group.level > 0 ? (isDark ? '#374151' : 'var(--gray-50)') : (isDark ? '#1f2937' : 'white') }}>
          <td style={{ padding: '0.75rem', paddingLeft: `${indent + 0.75}rem`, color: isDark ? '#f9fafb' : 'inherit' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {hasChildren && (
                <button
                  onClick={() => toggleExpand(group.id)}
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    fontSize: '1rem',
                    color: isDark ? '#9ca3af' : 'inherit'
                  }}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              )}
              <span style={{ marginLeft: hasChildren ? 0 : '1.75rem' }}>
                {group.name}
              </span>
            </div>
          </td>
          <td style={{ padding: '0.75rem', color: isDark ? '#d1d5db' : 'inherit' }}>{group.slug}</td>
          <td style={{ padding: '0.75rem', color: isDark ? '#d1d5db' : 'inherit' }}>{group.level}</td>
          <td style={{ padding: '0.75rem', color: isDark ? '#d1d5db' : 'inherit' }}>{group.product_count || 0}</td>
          <td style={{ padding: '0.75rem' }}>
            <span className={`badge badge-${group.is_active ? 'success' : 'secondary'}`}>
              {group.is_active ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
            <button onClick={() => handleCreate(group.id)} className="btn btn-sm btn-outline" style={{ marginRight: '0.5rem', fontSize: '0.75rem' }} title="Add Sub-group">
              ➕
            </button>
            <button onClick={() => handleEdit(group)} className="btn btn-sm btn-outline" style={{ marginRight: '0.5rem' }}>
              Edit
            </button>
            <button onClick={() => handleDelete(group.id)} className="btn btn-sm btn-danger">
              Delete
            </button>
          </td>
        </tr>
        {isExpanded && group.children.map(child => renderGroupRow(child))}
      </React.Fragment>
    );
  };

  const hierarchicalGroups = buildHierarchy(groups);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Product Categories</span>
      </nav>

      {/* Main Card */}
      <div className="card" style={{ background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: isDark ? '#f9fafb' : 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              📁 Product Categories
            </h2>
            <p style={{ color: isDark ? '#9ca3af' : 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Manage hierarchical product categories and groups
            </p>
          </div>
          <button onClick={() => handleCreate()} className="btn btn-primary">➕ Add Root Category</button>
        </div>

        {error && (
          <div style={{ backgroundColor: 'var(--red-50)', border: '1px solid var(--red-200)', color: 'var(--red-800)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setError(null)}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ backgroundColor: 'var(--success-50)', border: '1px solid var(--success-500)', color: 'var(--success-700)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setSuccess(null)}>
            {success}
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: isDark ? '#374151' : 'var(--gray-50)', borderBottom: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}` }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: isDark ? '#e5e7eb' : 'var(--gray-700)' }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: isDark ? '#e5e7eb' : 'var(--gray-700)' }}>Slug</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: isDark ? '#e5e7eb' : 'var(--gray-700)' }}>Level</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: isDark ? '#e5e7eb' : 'var(--gray-700)' }}>Products</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: isDark ? '#e5e7eb' : 'var(--gray-700)' }}>Status</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: isDark ? '#e5e7eb' : 'var(--gray-700)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {hierarchicalGroups.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
                  No product categories found
                </td>
              </tr>
            ) : (
              hierarchicalGroups.map(group => renderGroupRow(group))
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      {showDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '90%', maxWidth: '700px', maxHeight: '90vh', overflow: 'auto', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600', color: isDark ? '#f9fafb' : 'inherit' }}>
              {currentGroup ? 'Edit Product Group' : 'New Product Group'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#e5e7eb' : 'inherit' }}>
                    Group Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="form-input"
                    placeholder="Electronics"
                    style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#e5e7eb' : 'inherit' }}>
                    Slug *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    className="form-input"
                    placeholder="electronics"
                    style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#e5e7eb' : 'inherit' }}>
                  Description
                </label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="form-input"
                  placeholder="Group description"
                  style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#e5e7eb' : 'inherit' }}>
                    Parent Group
                  </label>
                  <select
                    value={formData.parent_id || ''}
                    onChange={(e) => setFormData({...formData, parent_id: e.target.value ? parseInt(e.target.value) : null})}
                    className="form-input"
                    style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                  >
                    <option value="">None (Root Level)</option>
                    {groups
                      .filter(g => !currentGroup || g.id !== currentGroup.id)
                      .map(g => (
                        <option key={g.id} value={g.id}>
                          {'\u00A0'.repeat((g.level || 0) * 4)}{g.name}
                        </option>
                      ))
                    }
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#e5e7eb' : 'inherit' }}>
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({...formData, sort_order: e.target.value})}
                    className="form-input"
                    placeholder="0"
                    style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#e5e7eb' : 'inherit' }}>
                  Image URL
                </label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  className="form-input"
                  placeholder="https://example.com/image.jpg"
                  style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                />
              </div>

              <div style={{ marginBottom: '1rem', paddingTop: '1rem', borderTop: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem', color: isDark ? '#f9fafb' : 'inherit' }}>SEO Settings</h4>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#e5e7eb' : 'inherit' }}>
                    SEO Title
                  </label>
                  <input
                    type="text"
                    value={formData.seo_title}
                    onChange={(e) => setFormData({...formData, seo_title: e.target.value})}
                    className="form-input"
                    placeholder="SEO optimized title"
                    style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#e5e7eb' : 'inherit' }}>
                    SEO Description
                  </label>
                  <textarea
                    rows="2"
                    value={formData.seo_description}
                    onChange={(e) => setFormData({...formData, seo_description: e.target.value})}
                    className="form-input"
                    placeholder="SEO meta description"
                    style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  <span style={{ fontSize: '0.875rem', fontWeight: '500', color: isDark ? '#e5e7eb' : 'inherit' }}>Active</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowDialog(false)} className="btn btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {currentGroup ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCategories;
